import type { Side } from '../pose/landmarks';

export type PunchType = 'jab' | 'cross' | 'hook' | 'uppercut' | 'overhand';
export type Target = 'head' | 'body';
/** Standard boxing numbering. 1 jab, 2 cross, 3/4 hooks, 5/6 uppercuts. */
export type PunchNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface PunchEvent {
  id: number;
  side: Side;
  isLead: boolean;
  type: PunchType;
  number: PunchNumber;
  target: Target;
  /** ms timestamps. `tPeak` is the moment of maximum extension — the "landing" instant. */
  tStart: number;
  tPeak: number;
  tEnd: number;
  /** Extension at the peak, 0..1. */
  extension: number;
  /** Peak fist speed in shoulder-widths/second. */
  speed: number;
  /** How far in front of the chest the fist reached, sw. Feeds range checks. */
  reach: number;
  /** Lateral fist position at peak, sw (subject's right positive). */
  lateral: number;
  /** Vertical fist position at peak relative to the shoulder line, sw. */
  height: number;
  /** Torso rotation swept during the punch, degrees. Proxy for hip drive. */
  hipDrive: number;
  /** How far the hand dropped/cocked before firing, sw. High = telegraphed. */
  telegraph: number;
  /** ms from peak back to guard. Slow = punished by counters. */
  recoveryMs: number;
  /** Classifier confidence, folded from pose quality and axis-dominance margin. */
  confidence: number;
}

export type DefenseKind = 'slip' | 'duck' | 'roll' | 'lean' | 'pull' | 'block' | 'parry' | 'shell';

export interface DefenseEvent {
  id: number;
  kind: DefenseKind;
  /** Direction of head travel for evasions; blocking side for blocks. */
  side: Side | 'centre';
  tStart: number;
  tPeak: number;
  tEnd: number;
  /** Peak displacement from guard-home, sw. */
  depth: number;
  /** Peak head speed, sw/s. Distinguishes a sharp slip from drifting. */
  speed: number;
  confidence: number;
}

export type FootworkKind = 'step-in' | 'step-out' | 'step-left' | 'step-right' | 'pivot-left' | 'pivot-right';

export interface FootworkEvent {
  id: number;
  kind: FootworkKind;
  tStart: number;
  tEnd: number;
  /** Displacement in sw, or degrees for pivots. */
  magnitude: number;
  confidence: number;
}

export interface ComboEvent {
  id: number;
  punches: PunchEvent[];
  /** e.g. "1-2-3". */
  notation: string;
  tStart: number;
  tEnd: number;
  /** Mean inter-punch interval, ms. */
  tempoMs: number;
  /** Standard deviation of intervals — low means metronomic, mid means varied rhythm. */
  tempoJitterMs: number;
  /** Distinct punch numbers used / total punches. */
  variety: number;
}

/** Per-frame continuous state that hit resolution reads. Not an event. */
export interface GuardState {
  /** 0..1 per hand. */
  left: number;
  right: number;
  /** 1 when both hands are home and elbows tucked. */
  shell: number;
  /** Which side the head is currently displaced toward, and by how much. */
  headOffX: number;
  headOffY: number;
  headOffZ: number;
  /** Active evasion, if any. */
  evading: DefenseKind | null;
  /** How committed the current evasion is, 0..1. */
  evasionDepth: number;
  /**
   * Timestamps of the current *or most recent* evasion. These persist after the
   * movement ends, because a punch often lands just after the player has already
   * slipped and come back — and reaction time must be measured from when they
   * started moving, not from whether they happen to still be moving at impact.
   */
  lastEvasionStartT: number;
  lastEvasionPeakT: number;
  lastEvasionKind: DefenseKind | null;
  /** Peak displacement of the most recent evasion, sw. */
  lastEvasionDepth: number;
  /** Signed lateral head offset at that peak, sw. */
  lastEvasionOffX: number;
}

export interface RecogniserEvents {
  punch: PunchEvent;
  'punch-start': { side: Side; t: number };
  defense: DefenseEvent;
  footwork: FootworkEvent;
  combo: ComboEvent;
}

const NUMBERS: Record<string, PunchNumber> = {
  'lead-jab': 1,
  'rear-cross': 2,
  'lead-hook': 3,
  'rear-hook': 4,
  'lead-uppercut': 5,
  'rear-uppercut': 6,
  'lead-overhand': 3,
  'rear-overhand': 7,
};

export function punchNumber(type: PunchType, isLead: boolean): PunchNumber {
  const key = `${isLead ? 'lead' : 'rear'}-${type === 'jab' || type === 'cross' ? (isLead ? 'jab' : 'cross') : type}`;
  return NUMBERS[key] ?? (isLead ? 1 : 2);
}

export function notationOf(punches: PunchEvent[]): string {
  return punches.map((p) => (p.target === 'body' ? `${p.number}b` : `${p.number}`)).join('-');
}
